import { useEffect, useState } from 'react';
import { Button, CloseIcon, Input, cn } from '@playerbase/ui';
import {
  PUBLISHING_STORE_LABELS,
  storeHasIntegration,
  type PublishingDestination,
} from '@playerbase/types';

type DestinationRowProps = {
  destination: PublishingDestination;
  onRemove: () => void | Promise<void>;
  onUpdateUrl: (storeUrl: string | null) => void | Promise<void>;
};

export function DestinationRow({
  destination,
  onRemove,
  onUpdateUrl,
}: DestinationRowProps): JSX.Element {
  const [url, setUrl] = useState(destination.storeUrl ?? '');

  // Reset local input when the source-of-truth changes (e.g. after a save).
  useEffect(() => {
    setUrl(destination.storeUrl ?? '');
  }, [destination.id, destination.storeUrl]);

  const trimmed = url.trim();
  const persistedUrl = destination.storeUrl ?? '';
  const dirty = trimmed !== persistedUrl;

  const handleBlur = (): void => {
    if (!dirty) return;
    void onUpdateUrl(trimmed === '' ? null : trimmed);
  };

  const handleRemove = (): void => {
    if (!window.confirm(
      `Remove ${PUBLISHING_STORE_LABELS[destination.store]} as a publishing destination?`,
    )) {
      return;
    }
    void onRemove();
  };

  const hasIntegration = storeHasIntegration(destination.store);

  return (
    <div className="flex flex-col gap-2 rounded-sm border border-border bg-surface-2 p-3 sm:flex-row sm:items-center">
      <div className="min-w-[8rem] text-sm font-semibold text-text">
        {PUBLISHING_STORE_LABELS[destination.store]}
      </div>
      <Input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onBlur={handleBlur}
        placeholder="Store URL (optional)"
        aria-label={`Store URL for ${PUBLISHING_STORE_LABELS[destination.store]}`}
        className="flex-1"
      />
      <div className="flex items-center gap-2">
        {hasIntegration ? (
          // TODO(integrations): wire up the connect flow when account-level
          // store integrations land. Disabled placeholder for now.
          <Button variant="ghost" size="sm" disabled aria-disabled>
            Connect to {PUBLISHING_STORE_LABELS[destination.store]}
          </Button>
        ) : (
          <span
            className={cn(
              'rounded-sm border border-border px-2 py-1',
              'font-mono text-[10px] uppercase tracking-wide text-text-muted',
            )}
          >
            No integration available
          </span>
        )}
        <button
          type="button"
          onClick={handleRemove}
          aria-label={`Remove ${PUBLISHING_STORE_LABELS[destination.store]}`}
          className={cn(
            'inline-flex h-7 w-7 items-center justify-center rounded-sm text-text-muted',
            'hover:bg-surface hover:text-text',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          )}
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );
}
